import { useCallback, useEffect, useState } from 'react'
import Link from 'next/link'

import {
  getStoredEvChargingBookings,
  isEvChargingBookingItem,
  updateEvChargingBookingItemCount as updateEvChargingBookingItemCountInStorage,
  type StoredEvChargingBooking,
} from '@/apps/categories/ev-charging'
import { useBookingQueryBundle } from '@/entities/booking'
import { useConvertedPriceFormatter } from '@/entities/price'
import { EvChargingBookingItem } from '@/shared/api'
import { cn } from '@/shared/lib/cn'
import { dayjs } from '@/shared/lib/datetime'
import { useTranslation } from '@/shared/lib/i18n'
import { PAGE_URL } from '@/shared/routes'
import { Badge } from '@/shared/ui/badge/badge'
import { Button_Modern } from '@/shared/ui/button_modern'
import { Icon } from '@/shared/ui/icon'
import { Message } from '@/shared/ui/message'

import { useBookingItemCountSync } from './use-booking-item-count-sync'

/**
 * Hook to manage EV charging bookings from localStorage
 * @returns Object with bookings array and helper methods
 */
export const useEvChargingBookings = () => {
  const [bookings, setBookings] = useState<StoredEvChargingBooking[]>(() => {
    return getStoredEvChargingBookings()
  })

  const hasBookings = bookings.length > 0

  const removeBooking = (bookingId: string) => {
    setBookings((prev) => prev.filter((b) => b.bookingId !== bookingId))
  }

  const updateBookingItemCount = (bookingId: string, itemCount: number) => {
    updateEvChargingBookingItemCountInStorage(bookingId, itemCount)
    setBookings((prev) => prev.map((b) => (b.bookingId === bookingId ? { ...b, itemCount } : b)))
  }

  return { bookings, hasBookings, removeBooking, updateBookingItemCount }
}

type ChargingWindowStatus = 'waiting' | 'active' | 'expired'

type EvChargingResultData = EvChargingBookingItem['booking_result_data'] & {
  physical_reference?: string
  evse_uid?: string
}

interface UpcomingEvChargingItemProps {
  bookingItem: EvChargingBookingItem
  bookingId: string
  onItemExpired: () => void
}

const UpcomingEvChargingItem: React.FC<UpcomingEvChargingItemProps> = ({ bookingItem, bookingId, onItemExpired }) => {
  const { t } = useTranslation()
  const formatPrice = useConvertedPriceFormatter()
  const [currentTime, setCurrentTime] = useState(dayjs())

  // Update current time every second for accurate countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(dayjs())
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const { item_data, booking_data } = bookingItem
  const resultData = bookingItem.booking_result_data as EvChargingResultData
  const physicalReference = resultData?.physical_reference || resultData?.evse_uid

  // Calculate charging window times (same logic as EvChargingActionsWidget)
  const chargingWindowStart = bookingItem.booking_result_data?.charging_window_start
    ? dayjs(bookingItem.booking_result_data.charging_window_start)
    : dayjs(bookingItem.created_at).add(booking_data.start_time, 'minutes')

  const chargingWindowEnd = bookingItem.booking_result_data?.charging_window_end
    ? dayjs(bookingItem.booking_result_data.charging_window_end)
    : chargingWindowStart.add(30, 'minutes')

  // Determine charging window status
  const getChargingWindowStatus = (): ChargingWindowStatus => {
    if (currentTime.isBefore(chargingWindowStart)) {
      return 'waiting'
    }
    if (currentTime.isAfter(chargingWindowStart) && currentTime.isBefore(chargingWindowEnd)) {
      return 'active'
    }
    return 'expired'
  }

  const chargingWindowStatus = getChargingWindowStatus()

  // Calculate time until charging window starts
  const timeUntilWindow = chargingWindowStart.diff(currentTime)
  const totalSecondsUntilWindow = Math.max(0, Math.ceil(timeUntilWindow / 1000))
  const minutesUntilWindow = Math.floor(totalSecondsUntilWindow / 60)
  const secondsUntilWindow = totalSecondsUntilWindow % 60

  // Calculate window duration in minutes
  const windowDurationMinutes = chargingWindowEnd.diff(chargingWindowStart, 'minutes')

  // Format window duration display
  const getWindowDurationDisplay = () => {
    if (windowDurationMinutes >= 60) {
      const hours = Math.floor(windowDurationMinutes / 60)
      const mins = windowDurationMinutes % 60
      if (mins === 0) {
        return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
      }
      return `${hours}h ${mins}min`
    }
    return `${windowDurationMinutes} min`
  }

  // Format countdown display
  const getCountdownDisplay = () => {
    if (minutesUntilWindow >= 60) {
      const hours = Math.floor(minutesUntilWindow / 60)
      const mins = minutesUntilWindow % 60
      return `${hours}:${mins.toString().padStart(2, '0')}:${secondsUntilWindow.toString().padStart(2, '0')}`
    }
    return `${minutesUntilWindow}:${secondsUntilWindow.toString().padStart(2, '0')} min.`
  }

  const canStartSession = chargingWindowStatus === 'active'
  const isExpired = chargingWindowStatus === 'expired'

  // Handle expired items - notify parent to update count
  useEffect(() => {
    if (isExpired) {
      onItemExpired()
    }
  }, [isExpired, onItemExpired])

  // Don't show expired bookings
  if (isExpired) {
    return null
  }

  const totalPrice = item_data.price ?? 0

  return (
    <div className="w-full rounded-4 bg-neutral-fill-surface-primary p-4">
      {/* Header with status badge and total */}
      <div className="mb-3 flex items-center justify-between">
        <Badge size="small" className="bg-success-fill-stroke-secondary text-success">
          {t('myBookings.status.booked')}
        </Badge>
        <span className="text-gray-500 text-sm">
          Total <span className="text-gray-900 text-lg font-semibold">{formatPrice(totalPrice)}</span>
        </span>
      </div>

      {/* Station info */}
      <div className="mb-3 flex items-stretch gap-3">
        <div
          className="flex w-[80px] flex-shrink-0 items-center justify-center rounded-4 bg-neutral-fill-background-secondary"
          aria-hidden
        >
          <Icon name="evStation" className="text-neutral-text-icons-disabled" size={24} />
        </div>

        <div className="grid min-w-0 flex-1 gap-y-2">
          <h3 className="truncate text-xl font-semibold">{item_data.name}</h3>
          <div className="flex items-center gap-1 text-sm">
            <Icon name="Feather.Clock" size={14} />
            <span>
              {chargingWindowStart.format('h:mm A')} – {chargingWindowEnd.format('h:mm A')} ({getWindowDurationDisplay()})
            </span>
          </div>
          {physicalReference && (
            <Message theme="brand" size="small" className="!rounded-2">
              <span className="font-semibold">{physicalReference}</span>
            </Message>
          )}
        </div>
      </div>

      {/* Countdown / Start button */}
      <div className="mb-3">
        {canStartSession ? (
          <Link href={PAGE_URL.bookingConfirmCharging(bookingId)} className="block">
            <Button_Modern variant="primary" size="large" className="w-full">
              Start Charging Session
            </Button_Modern>
          </Link>
        ) : (
          <Button_Modern variant="primary" size="large" className="w-full" disabled>
            Start Charging in {getCountdownDisplay()}
          </Button_Modern>
        )}
      </div>

      {/* Open Details link */}
      <div className="text-center">
        <Link href={PAGE_URL.bookingConfirmCharging(bookingId)} className="text-sm font-medium text-brand hover:underline">
          Open Details
        </Link>
      </div>
    </div>
  )
}

// Component to load and display a single booking from localStorage
const UpcomingEvChargingBookingLoader: React.FC<{
  booking: StoredEvChargingBooking
  onRemove: () => void
  onUpdateItemCount: (itemCount: number) => void
}> = ({ booking, onRemove, onUpdateItemCount }) => {
  const { query: bookingQuery } = useBookingQueryBundle(booking.bookingId)
  const [expiredItemIds, setExpiredItemIds] = useState<Set<string>>(new Set())

  const handleItemExpired = useCallback((itemId: string) => {
    setExpiredItemIds((prev) => new Set(prev).add(itemId))
  }, [])

  // Filter EV charging items that are booked and reserved
  const evChargingItems =
    bookingQuery.data?.booking_items?.filter(isEvChargingBookingItem).filter((item) => {
      const isBooked = item.status === 'Booked'
      const isReserved = !item.booking_result_data || item.booking_result_data.charging_status === 'reserved'
      return isBooked && isReserved
    }) ?? []

  const activeItems = evChargingItems.filter((item) => !expiredItemIds.has(item.id))

  // Sync item count changes and handle booking removal when all items expire
  useBookingItemCountSync({
    bookingQuery,
    activeItemsCount: activeItems.length,
    booking,
    onRemove,
    onUpdateItemCount,
  })

  // Show nothing while loading or on error
  if (bookingQuery.isLoading || bookingQuery.isError) {
    return null
  }

  if (activeItems.length === 0) {
    return null
  }

  return (
    <>
      {activeItems.map((item) => (
        <UpcomingEvChargingItem
          key={item.id}
          bookingItem={item}
          bookingId={booking.bookingId}
          onItemExpired={() => handleItemExpired(item.id)}
        />
      ))}
    </>
  )
}

export const UpcomingEvChargingWidget: React.FC = () => {
  const { bookings, removeBooking, updateBookingItemCount } = useEvChargingBookings()

  // Don't render if no stored bookings
  if (bookings.length === 0) {
    return null
  }

  const totalItems = bookings.reduce((sum, booking) => sum + booking.itemCount, 0)

  return (
    <div
      className={cn(
        'relative mx-auto mt-4 px-4 desktop:row-start-3 desktop:px-0',
        totalItems >= 2 ? 'max-w-[940px]' : 'max-w-[438px]',
      )}
    >
      <div className="bg-white/2 rounded-6 border border-white/50 p-4 backdrop-blur-[50px]">
        <h2 className="mb-4 text-lg font-semibold text-white">Upcoming Events</h2>
        <div className={cn('grid w-full gap-4', totalItems >= 2 ? 'desktop:grid-cols-2' : 'grid-cols-1')}>
          {bookings.map((booking) => (
            <UpcomingEvChargingBookingLoader
              key={booking.bookingId}
              booking={booking}
              onRemove={() => removeBooking(booking.bookingId)}
              onUpdateItemCount={(itemCount) => updateBookingItemCount(booking.bookingId, itemCount)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
